import { createElement, type ReactNode } from 'react'

import { cn } from '@/lib/utils'

// --- SectionHeader ---

interface SectionHeaderProps {
  align?: 'center' | 'left' | 'split'
  children: ReactNode
  className?: string
}

function SectionHeader({ align = 'center', children, className }: SectionHeaderProps) {
  return (
    <div
      data-slot="section-header"
      data-align={align}
      className={cn(
        'space-y-3',
        align === 'center' && 'text-center',
        align === 'split' &&
          'md:grid md:grid-cols-2 md:items-start md:gap-x-8 md:gap-y-3 md:space-y-0',
        className
      )}
    >
      {children}
    </div>
  )
}

// --- SectionBadge ---

interface SectionBadgeProps {
  children: ReactNode
  dot?: boolean
  className?: string
}

function SectionBadge({ children, dot, className }: SectionBadgeProps) {
  return (
    <p
      data-slot="section-badge"
      className={cn(
        'type-overline inline-flex items-center leading-none text-muted-foreground md:col-span-2',
        className
      )}
    >
      {dot && (
        <span
          className="mr-2 inline-block size-1.5 shrink-0 rounded-full bg-neon"
          aria-hidden="true"
        />
      )}
      <span>{children}</span>
    </p>
  )
}

// --- SectionTitle ---

interface SectionTitleProps {
  as?: 'h1' | 'h2' | 'h3'
  children: ReactNode
  className?: string
}

function SectionTitle({ as = 'h2', children, className }: SectionTitleProps) {
  return createElement(
    as,
    {
      'data-slot': 'section-title',
      className: cn('type-h2 text-foreground', className),
    },
    children
  )
}

// --- SectionSubtitle ---

interface SectionSubtitleProps {
  children: ReactNode
  className?: string
}

function SectionSubtitle({ children, className }: SectionSubtitleProps) {
  return (
    <p data-slot="section-subtitle" className={cn('text-muted-foreground', className)}>
      {children}
    </p>
  )
}

// --- Exports ---

export { SectionHeader, SectionBadge, SectionTitle, SectionSubtitle }
